import React, { useCallback, useEffect, useState } from 'react';
import { Box, Dialog, DialogTitle, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassIconButton, GlassTabs, LoadingOverlay } from 'dashboard/components/common';
import AccountProfileTab from './AccountProfileTab';
import AccountPasswordTab from './AccountPasswordTab';
import AccountDangerTab from './AccountDangerTab';

/**
 * "My account" — the signed-in person, in a dialog.
 *
 * Deliberately NOT a page under Brand Manager. A user and a brand are
 * different objects: you have one account and may have several brands, and
 * mixing them is how a brand's settings page ended up offering to "delete
 * this account". A dialog also matches how rarely this is used — you change
 * your password and leave, rather than navigating away from the brand you
 * were working on.
 */
export default function AccountDialog({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return API.getUserProfile()
      .then((res) => setProfile(res && typeof res === 'object' ? res : null))
      .catch((err) => toast.error(err?.message || 'Could not load your account'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const displayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ')
    || profile?.username
    || profile?.email
    || 'Your account';

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: '52px' }}>
        My account
        <Typography variant='caption' color='text.secondary' display='block' sx={{ fontWeight: 400 }}>
          {displayName}
        </Typography>
        <GlassIconButton
          label='Close'
          onClick={onClose}
          size='small'
          sx={{ position: 'absolute', right: 12, top: 14 }}
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>

      <Box sx={{ px: '24px', pt: '4px' }}>
        <GlassTabs value={tab} onChange={(_, v) => setTab(v)} tabs={['Profile', 'Password', 'Close account']} />
      </Box>

      <Box sx={{ p: '20px 24px 24px' }}>
        <LoadingOverlay loading={loading} minHeight={200}>
          {tab === 0 && <AccountProfileTab profile={profile} onSaved={load} />}
          {tab === 1 && <AccountPasswordTab onDone={onClose} />}
          {tab === 2 && <AccountDangerTab onClosed={onClose} />}
        </LoadingOverlay>
      </Box>
    </Dialog>
  );
}
